import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePermissions } from '../../hooks/usePermissions';
import PartHistoryDialog from './components/PartHistoryDialog';
import DealersStockFilter from './components/DealersStockFilter';
import DealerPartsTable from './components/DealerPartsTable';
import useDealerPartsData from './hooks/useDealerPartsData';
import usePartHistoryData from './hooks/usePartHistoryData';
import GenericFileExportButton from '../../components/common/GenericFileExportButton';
import { useLazyExportDealersStockViewToExcelQuery } from '../../api/endpoints/dealersStockView';

const DealersStockViewPage = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const { hasDealerRole } = usePermissions();
    const isDealer = hasDealerRole();

    const { selection, selectAddress, rows, totalCount, loading, pagination, onPageChange, applyFilters, partCodeFilter, partDescFilter, blockFilter, shelfFilter, sequenceFilter, eyesFilter } = useDealerPartsData(isDealer);
    const {
        visible: historyVisible,
        openWithRow: openHistoryWithRow,
        close: closeHistory,
        rows: historyRows,
        totalCount: historyTotal,
        pagination: historyPagination,
        onPageChange: onHistoryPageChange,
        loading: historyLoading,
        headerInfo: historyHeader
    } = usePartHistoryData();

    const handleFilterSelect = useCallback(
        ({ dealer, address }) => {
            const dealerId = dealer?.id ?? dealer?.dealerId ?? dealer?.erpDealerId ?? dealer?.ErpDealerId;
            const dealerAddressId = address?.addrId ?? address?.id;

            selectAddress({
                dealerId,
                dealerAddressId,
                dealerName: dealer?.displayValue || dealer?.name || dealer?.dealerName || '',
                addressName: address?.addressName || address?.display || ''
            });
        },
        [selectAddress]
    );

    const handleApplyFilters = useCallback((partCode, partDesc, block, shelf, sequence, eyes) => {
        applyFilters(partCode, partDesc, block, shelf, sequence, eyes);
    }, [applyFilters]);

    const langId = getCurrentLanguageId();
    const exportParams = useMemo(() => ({
        LanguageId: typeof langId === 'number' ? langId : 2,
        CompressionType: 1,
        ...(selection?.dealerId ? { DealerId: selection.dealerId } : {}),
        ...(selection?.dealerAddressId ? { DealerAddressId: selection.dealerAddressId } : {}),
        ...(partCodeFilter ? { partCode: partCodeFilter } : {}),
        ...(partDescFilter ? { partDesc: partDescFilter } : {}),
        ...(blockFilter ? { block: blockFilter } : {}),
        ...(shelfFilter ? { shelf: shelfFilter } : {}),
        ...(sequenceFilter ? { sequence: sequenceFilter } : {}),
        ...(eyesFilter ? { eyes: eyesFilter } : {})
    }), [langId, selection?.dealerId, selection?.dealerAddressId, partCodeFilter, partDescFilter, blockFilter, shelfFilter, sequenceFilter, eyesFilter]);

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('dealersStockView.pageTitle') || 'Bayi Stok ve Kullanım Geçmişi'}</h1>
                <GenericFileExportButton
                    useExportQuery={useLazyExportDealersStockViewToExcelQuery}
                    exportParams={exportParams}
                    buttonLabel={th('common.export') || 'Excel'}
                    buttonClassName="p-button-success"
                    icon="pi pi-file-excel"
                    fileNameExtractor={(result) => result?.fileName || 'bayi_stok_rapor.xlsx'}
                    dataExtractor={(result) => result?.fileBase64}
                    contentTypeExtractor={(result) => result?.contentType}
                />
            </div>

            {!isDealer && <DealersStockFilter initiallyOpen onSelect={handleFilterSelect} onApplyFilters={handleApplyFilters} />}
            {isDealer && <DealersStockFilter initiallyOpen showPartCodeOnly onApplyFilters={handleApplyFilters} />}

            {(isDealer || (selection?.dealerId && selection?.dealerAddressId)) && (
                <DealerPartsTable dealerName={selection?.dealerName} addressName={selection?.addressName} rows={rows} totalCount={totalCount} pagination={pagination} onPageChange={onPageChange} loading={loading} onOpenHistory={openHistoryWithRow} />
            )}
            <PartHistoryDialog visible={historyVisible} onHide={closeHistory} rows={historyRows} totalCount={historyTotal} pagination={historyPagination} onPageChange={onHistoryPageChange} loading={historyLoading} headerInfo={historyHeader} />
        </div>
    );
};

export default DealersStockViewPage;
